import type {
  LogicalNodeKind,
  KrsNode,
  KrsEdge,
  KrsFile,
  HandlesTarget,
  NodeIdPath,
  ResourceNode,
  EntityNode,
  TeamNode,
  LegendRefTarget,
} from "../types/ast.js";
import { INFRA_KIND_SET, OWNS_TARGET_KIND_SET, REALIZES_TARGET_KIND_SET } from "../types/ast.js";
import { collectDeclaredNodePaths, resolveDeclaredRef } from "../parser/reference-validation.js";
import { nodePathIdentityKey, nodePathKey, nodePathMatchesSuffix } from "../parser/node-path.js";
import { isWriteOperation, isReadOperation } from "../spec/operations.js";
import type { StyleSheet, StyleSelector } from "../types/style.js";
import type { Warning } from "../types/warnings.js";
import { collectLegendUsage, legendRefHasUsage } from "../legend/usage.js";
import { facetWalkRoots } from "../renderer/facet-overlay.js";
import { edgeBaseId } from "./canonical-id.js";
import {
  buildEdgeEndpointIndex,
  edgeEndpointRef,
  resolveEdgeEndpoint,
  type EdgeEndpointIndex,
} from "./edge-endpoint.js";
import { buildEntityResolver } from "./resource-entity.js";
import { REFERENCE_DATA, LOGICAL_CONTAINMENT } from "../builtins/reference-data.js";
import { formatSelector } from "../style/serialize.js";

export function analyze(file: KrsFile, sheets: StyleSheet[], systemSheetCount = 1): Warning[] {
  const warnings: Warning[] = [];
  // Built once per pass: detectAnnotationPossibleTypos and
  // detectUnresolvedLegendRefs both consume the same selector index, and
  // analyze() runs per keystroke (app) / per document change (LSP).
  const stylesIndex = indexStyleSelectors(sheets);
  // One scope walk shared by the two endpoint detectors, lazy for the same
  // reason `declaredNodePathsOnce` is: a file with no dotted target and no
  // out-of-scope endpoint never needs it.
  let edgeScopeIndex: EdgeEndpointIndex | undefined;
  const edgeScopeIndexOnce = (): EdgeEndpointIndex =>
    (edgeScopeIndex ??= buildEdgeEndpointIndex(file));

  warnings.push(...detectDomainDispersal(file));
  warnings.push(...detectSharedInfraFanIn(file));
  warnings.push(...detectCrossDomainStoreAccess(file));
  warnings.push(...detectUnassignedDomains(file));
  warnings.push(...detectUnassignedServices(file));
  warnings.push(...detectUnassignedClients(file));
  warnings.push(...detectUnresolvedHandles(file));
  warnings.push(...detectUnassignedDatabases(file));
  warnings.push(...detectUnassignedQueues(file));
  warnings.push(...detectUnassignedStorages(file));
  warnings.push(...detectUnassignedUsecases(file));
  warnings.push(...detectUnassignedResources(file));
  warnings.push(...detectEntityAnchorCollisions(file));
  warnings.push(...detectStyleConflicts(sheets, systemSheetCount));
  warnings.push(...detectMissingProperties(file));
  warnings.push(...detectUnresolvedRealizes(file));
  warnings.push(...detectInvalidOwns(file));
  warnings.push(...detectCrossSystemRefs(file, edgeScopeIndexOnce));
  warnings.push(...detectUnresolvedEdgeEndpoints(file));
  warnings.push(...detectEdgeEndpointsNotAtScope(file, edgeScopeIndexOnce));
  warnings.push(...detectCyclicDependencies(file));
  warnings.push(...detectDeliversTargetNotClient(file));
  warnings.push(...detectDuplicateClientCapabilities(file));
  warnings.push(...detectAnnotationPossibleTypos(file, stylesIndex));
  warnings.push(...detectTagsNotBuiltin(file));
  warnings.push(...detectTagsNotApplicable(file));
  warnings.push(...detectAnnotationsNotBuiltin(file));
  warnings.push(...detectStyleSelectorsNotBuiltin(sheets, systemSheetCount));
  warnings.push(...detectFacetsNotDeclared(file));
  warnings.push(...detectUnresolvedLegendRefs(file, stylesIndex));

  return warnings;
}

/**
 * Each `ref` entry in a `legend` block must resolve through the existing
 * style cascade. Emit a warning when the target is not present in any
 * node (annotations / tags / ids / kinds) or style rule selector.
 */
function detectUnresolvedLegendRefs(file: KrsFile, stylesIndex: StyleSelectorIndex): Warning[] {
  if (file.legends.length === 0) return [];

  // Single source of truth for "is this ref's target in use". The renderer
  // consumes the same helper (see legend/usage.ts and svg-builder.ts) so
  // both layers agree on what "resolved" means.
  const usage = collectLegendUsage(file);

  const warnings: Warning[] = [];
  for (const legend of file.legends) {
    for (const entry of legend.entries) {
      if (entry.kind !== "ref") continue;
      if (
        legendRefHasUsage(entry.target, usage) ||
        legendRefMatchesStyle(entry.target, stylesIndex)
      ) {
        continue;
      }
      warnings.push({
        kind: "legend-ref-unresolved",
        params: {
          target: stringifyLegendRefTarget(entry.target),
          ...(legend.title ? { legendTitle: legend.title } : {}),
        },
        loc: entry.loc,
      });
    }
  }
  return warnings;
}

interface StyleSelectorIndex {
  annotations: Set<string>;
  tags: Set<string>;
  ids: Set<string>;
  nodeTypes: Set<string>;
}

function indexStyleSelectors(sheets: StyleSheet[]): StyleSelectorIndex {
  const annotations = new Set<string>();
  const tags = new Set<string>();
  const ids = new Set<string>();
  const nodeTypes = new Set<string>();
  for (const sheet of sheets) {
    for (const rule of sheet.rules) {
      const sel: StyleSelector = rule.selector;
      if (sel.id) ids.add(sel.id);
      if (sel.nodeType) nodeTypes.add(sel.nodeType);
      for (const a of sel.annotations) annotations.add(a);
      for (const t of sel.tags) tags.add(t);
    }
  }
  return { annotations, tags, ids, nodeTypes };
}

function legendRefMatchesStyle(target: LegendRefTarget, styles: StyleSelectorIndex): boolean {
  switch (target.kind) {
    case "annotation":
      return styles.annotations.has(target.name);
    case "tag":
      return styles.tags.has(target.name);
    case "selector": {
      const sel = target.selector;
      if (sel.startsWith("#")) return styles.ids.has(sel.slice(1));
      // `.class` selectors are accepted by the parser for forward
      // compatibility, but `.krs.style` does not have CSS-style classes
      // (see docs/spec/style.md). Always unresolved for now.
      if (sel.startsWith(".")) return false;
      // Bare type selector (`service`, `domain`, etc.).
      return styles.nodeTypes.has(sel);
    }
  }
}

function stringifyLegendRefTarget(target: LegendRefTarget): string {
  switch (target.kind) {
    case "annotation":
      return `@${target.name}`;
    case "tag":
      return `[${target.name}]`;
    case "selector":
      return target.selector;
  }
}

function detectDeliversTargetNotClient(file: KrsFile): Warning[] {
  const clientIds = new Set<string>();
  const services: KrsNode[] = [];

  function walk(nodes: KrsNode[]): void {
    for (const node of nodes) {
      if (node.kind === "client") clientIds.add(node.id);
      if (node.kind === "service") services.push(node);
      walk(node.children);
    }
  }
  for (const system of file.systems) walk(system.children);
  walk(file.services);
  for (const client of file.clients) clientIds.add(client.id);

  const warnings: Warning[] = [];
  for (const service of services) {
    const delivers = (service as KrsNode & { properties: { delivers?: string[] } }).properties
      .delivers;
    if (!delivers) continue;
    for (const targetId of delivers) {
      if (!clientIds.has(targetId)) {
        warnings.push({
          kind: "delivers-target-not-client",
          params: { serviceId: service.id, targetId },
          loc: service.loc,
        });
      }
    }
  }
  return warnings;
}

function detectDuplicateClientCapabilities(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];
  const visit = (node: KrsNode): void => {
    if (node.kind === "client") {
      const seen = new Map<string, true>();
      for (const cap of node.properties.capabilities) {
        if (seen.has(cap.name)) {
          warnings.push({
            kind: "client-capability-duplicate",
            params: { clientId: node.id, name: cap.name },
            loc: cap.loc,
          });
        } else {
          seen.set(cap.name, true);
        }
      }
    }
    for (const child of node.children) visit(child);
  };
  for (const system of file.systems) for (const child of system.children) visit(child);
  for (const client of file.clients) visit(client);
  for (const service of file.services) visit(service);
  return warnings;
}

/**
 * Any annotation identifier still parses in v1.x (docs/spec/tags-annotations.md
 * § Non-builtin annotation names are deprecated (v1.x)). This hint surfaces a
 * *near-miss* of a built-in name — `@depracated` silently losing its badge is
 * the failure mode it exists for (#1499). Fires as `info`, never `warning`.
 *
 * A name that appears in any stylesheet annotation selector is treated as
 * intentionally user-defined and is never hinted, even when it sits close
 * to a built-in. The unconditional v1.x deprecation of non-builtin names is
 * `detectAnnotationsNotBuiltin` (#2159); both coexist during v1.x and are
 * consolidated in v2.0.
 */
function detectAnnotationPossibleTypos(file: KrsFile, stylesIndex: StyleSelectorIndex): Warning[] {
  const builtins = REFERENCE_DATA.annotations.map((a) => a.name);
  const styledAnnotations = stylesIndex.annotations;
  const warnings: Warning[] = [];

  const checkAnnotations = (annotations: string[], nodeId: string, loc: KrsNode["loc"]): void => {
    for (const annotation of annotations) {
      if (builtins.includes(annotation) || styledAnnotations.has(annotation)) continue;
      const suggestion = closestBuiltinAnnotation(annotation, builtins);
      if (suggestion !== undefined) {
        warnings.push({
          kind: "annotation-possible-typo",
          params: { nodeId, annotation, suggestion },
          loc,
        });
      }
    }
  };
  const visit = (node: KrsNode): void => {
    checkAnnotations(node.annotations, node.id, node.loc);
    for (const child of node.children) visit(child);
  };
  // `team` blocks accept the same annotation grammar (added by #1605, after
  // this hint) — walk them too so a near-miss on a team gets the same
  // suggestion it would get on a node, keeping parity with
  // `detectAnnotationsNotBuiltin` (spec: both diagnostics coexist in v1.x).
  const visitTeam = (team: TeamNode): void => {
    checkAnnotations(team.annotations, team.id, team.loc);
    for (const child of team.children) {
      if (child.kind === "team") visitTeam(child);
    }
  };

  for (const system of file.systems) visit(system);
  for (const client of file.clients) visit(client);
  for (const service of file.services) visit(service);
  for (const domain of file.domains) visit(domain);
  for (const database of file.databases) visit(database);
  for (const queue of file.queues) visit(queue);
  for (const storage of file.storages) visit(storage);
  for (const organization of file.organizations) {
    for (const team of organization.teams) visitTeam(team);
  }
  return warnings;
}

/**
 * Tags outside the tool vocabulary that are still legitimate in authored
 * source: the system-assigned tags of docs/spec/tags-annotations.md
 * § System-assigned tags. Most are synthesized after parsing and never
 * appear in `.krs` files, but `[inferred]` is stamped *into* the emitted
 * source by `translate --from db` and persists until curated away —
 * warning on these would flag the tool's own vocabulary as foreign.
 */
export const SYSTEM_ASSIGNED_TAGS = [
  "implicit",
  "cyclic",
  "read",
  "write",
  "inferred",
  "projected",
];

/**
 * A builtin tag written on a node kind outside its declared `appliesTo`
 * (#2225). `appliesTo` was declared for every builtin tag, exposed through
 * `reference.ts` and printed in the generated spec table, but no consumer
 * validated it — so `service Api [index]` parsed, rendered no badge, and
 * emitted nothing. That is TPL-1503's forbidden fourth state (accepted,
 * inert, undocumented) surviving in the **kind** dimension after #2159
 * resolved it in the **name** dimension; from the author's side the two are
 * indistinguishable ("I wrote a tag and nothing happened").
 *
 * Only builtin names are checked: a non-builtin name has no `appliesTo` to
 * violate and already draws `tag-not-builtin`, so warning twice on one tag
 * would say the same thing in two registers. System-assigned tags
 * ({@link SYSTEM_ASSIGNED_TAGS}) are absent from `REFERENCE_DATA.tags` and so
 * are skipped by the same lookup rather than by a second exclusion list.
 *
 * The shape tags inferred from infra sub-kinds (`table` / `queue-item` /
 * `bucket` → `[table]` / `[queue]` / `[storage]`) are derived in the style
 * resolver against dot-notation ids and never enter `node.tags`, so they
 * cannot reach this walk; `warnings.test.ts` pins that so a future change to
 * the inference path cannot start producing false positives here.
 */
function detectTagsNotApplicable(file: KrsFile): Warning[] {
  const applicability = new Map<string, string[]>(
    REFERENCE_DATA.tags.map((t) => [t.name, t.appliesTo]),
  );
  const warnings: Warning[] = [];

  const checkTags = (tags: string[], kind: string, nodeId: string, loc: KrsNode["loc"]): void => {
    for (const tag of tags) {
      const appliesTo = applicability.get(tag);
      if (!appliesTo || appliesTo.includes(kind)) continue;
      warnings.push({
        kind: "tag-not-applicable",
        params: { nodeId, tag, nodeKind: kind, appliesTo },
        loc,
      });
    }
  };
  const visit = (node: KrsNode): void => {
    checkTags(node.tags, node.kind, node.id, node.loc);
    // `[async]` / `[sync]` declare appliesTo: ["edge"], so edges are checked
    // against the literal kind "edge" rather than the node they hang off.
    for (const edge of node.edges) {
      checkTags(edge.tags, "edge", `${edge.from} -> ${edge.to}`, edge.loc);
    }
    for (const child of node.children) visit(child);
  };

  for (const system of file.systems) visit(system);
  for (const client of file.clients) visit(client);
  for (const service of file.services) visit(service);
  for (const domain of file.domains) visit(domain);
  for (const database of file.databases) visit(database);
  for (const queue of file.queues) visit(queue);
  for (const storage of file.storages) visit(storage);
  return warnings;
}

/**
 * v1.x deprecation for tag names outside the tool vocabulary
 * (ADR-2065 Part A, #2159). The name is still accepted
 * unchanged — ADR-1314 freezes bare-tag acceptance for v1.x — but syntax
 * v2.0 keeps only tool-owned tags, so every non-builtin use is warned now.
 * Unlike `annotation-possible-typo` there is deliberately **no suppression
 * condition**: a style selector or legend ref proves the name is intentional,
 * but intent does not change the v2.0 outcome, so the deprecation is
 * announced unconditionally. Resolves the TPL-1503 fourth state
 * (accepted, inert, undocumented) into state (2): warned as unknown.
 */
function detectTagsNotBuiltin(file: KrsFile): Warning[] {
  const allowed = new Set<string>([
    ...REFERENCE_DATA.tags.map((t) => t.name),
    ...SYSTEM_ASSIGNED_TAGS,
  ]);
  const warnings: Warning[] = [];

  const checkTags = (tags: string[], nodeId: string, loc: KrsNode["loc"]): void => {
    for (const tag of tags) {
      if (!allowed.has(tag)) {
        warnings.push({ kind: "tag-not-builtin", params: { nodeId, tag }, loc });
      }
    }
  };
  const visit = (node: KrsNode): void => {
    checkTags(node.tags, node.id, node.loc);
    for (const edge of node.edges) {
      checkTags(edge.tags, `${edge.from} -> ${edge.to}`, edge.loc);
    }
    for (const child of node.children) visit(child);
  };

  for (const system of file.systems) visit(system);
  for (const client of file.clients) visit(client);
  for (const service of file.services) visit(service);
  for (const domain of file.domains) visit(domain);
  for (const database of file.databases) visit(database);
  for (const queue of file.queues) visit(queue);
  for (const storage of file.storages) visit(storage);
  return warnings;
}

/**
 * v1.x deprecation for annotation names outside the builtin lifecycle
 * vocabulary — same contract as `detectTagsNotBuiltin` (accepted in v1.x,
 * tool vocabulary only in v2.0, no suppression condition). Subsumes the
 * near-miss case of `annotation-possible-typo`; both diagnostics coexist
 * during v1.x and are consolidated in v2.0. Also covers `team` blocks,
 * which accept the same annotation grammar (spec § Team annotations).
 */
function detectAnnotationsNotBuiltin(file: KrsFile): Warning[] {
  const builtins = new Set<string>(REFERENCE_DATA.annotations.map((a) => a.name));
  const warnings: Warning[] = [];

  const checkAnnotations = (annotations: string[], nodeId: string, loc: KrsNode["loc"]): void => {
    for (const annotation of annotations) {
      if (!builtins.has(annotation)) {
        warnings.push({ kind: "annotation-not-builtin", params: { nodeId, annotation }, loc });
      }
    }
  };
  const visit = (node: KrsNode): void => {
    checkAnnotations(node.annotations, node.id, node.loc);
    for (const child of node.children) visit(child);
  };
  const visitTeam = (team: TeamNode): void => {
    checkAnnotations(team.annotations, team.id, team.loc);
    for (const child of team.children) {
      if (child.kind === "team") visitTeam(child);
    }
  };

  for (const system of file.systems) visit(system);
  for (const client of file.clients) visit(client);
  for (const service of file.services) visit(service);
  for (const domain of file.domains) visit(domain);
  for (const database of file.databases) visit(database);
  for (const queue of file.queues) visit(queue);
  for (const storage of file.storages) visit(storage);
  for (const organization of file.organizations) {
    for (const team of organization.teams) visitTeam(team);
  }
  return warnings;
}

/**
 * v1.x deprecation for `.krs.style` **selectors** that target a tag or
 * annotation name outside the tool vocabulary (#2175, design
 * `tags-and-facets.md` (B8)).
 *
 * #2159 deprecated the names on the *model* side and deliberately stopped
 * there: at that point a sheet's `[pci] { … }` rule was the only way to style
 * a cross-cutting concern, and warning about it would have told the author to
 * stop doing the one thing that worked. Facet selectors (`[facets=<id>]`) are
 * the migration target, so the style-side half fires from the release that
 * ships them — never before.
 *
 * Both halves fire for one name, on purpose. `[pci]` written on a node and
 * `[pci] { … }` written in a sheet are two separate edits, and warning only
 * once would leave whichever site was quiet unfound.
 *
 * System sheets are skipped: the builtin theme and any injected theme sheets
 * are not the author's to edit, and they use builtin vocabulary by
 * construction. The `sourceIndex` split is the same one `detectStyleConflicts`
 * uses.
 *
 * Matching is untouched — v1.x keeps applying these rules (ADR-1314). Dropping
 * them now would silently change how existing models look, which is a v2.0
 * decision (design Part A step 4).
 */
function detectStyleSelectorsNotBuiltin(sheets: StyleSheet[], systemSheetCount: number): Warning[] {
  const allowedTags = new Set<string>([
    ...REFERENCE_DATA.tags.map((t) => t.name),
    ...SYSTEM_ASSIGNED_TAGS,
  ]);
  const allowedAnnotations = new Set<string>(REFERENCE_DATA.annotations.map((a) => a.name));
  const warnings: Warning[] = [];

  for (const sheet of sheets.slice(systemSheetCount)) {
    for (const rule of sheet.rules) {
      const selector = formatSelector(rule.selector);
      for (const tag of rule.selector.tags) {
        if (allowedTags.has(tag)) continue;
        warnings.push({
          kind: "style-tag-selector-not-builtin",
          params: { tag, selector },
          loc: rule.selector.loc,
        });
      }
      for (const annotation of rule.selector.annotations) {
        if (allowedAnnotations.has(annotation)) continue;
        warnings.push({
          kind: "style-annotation-selector-not-builtin",
          params: { annotation, selector },
          loc: rule.selector.loc,
        });
      }
    }
  }
  return warnings;
}

/**
 * A `facets <id>` reference must name a declared `facet` block (#2065 Part B).
 *
 * This is the resolver-side validator every cross-reference property is
 * required to ship with (TPL-20260510-10): the parser accepts any identifier,
 * so without this a one-character slip (`facets pcl`) would silently put the
 * element in a facet nobody ever looks at.
 *
 * It lives here, not in the parser, because the declaration and the reference
 * may sit in different files — `analyze()` runs on the import-merged model, so
 * the merged-space requirement (TPL-20260718-02) holds by construction rather
 * than by a suppress-and-re-derive pass.
 *
 * **Sheet-less / single-document context (TPL-20260612-01):** this warning is
 * *not* suppressed in the LSP. It is import-coupled, so a project that declares
 * its facets in one file and references them in another over-reports there —
 * the same trade `invalid-owns` and `unresolved-handles` already make. The
 * alternative costs more: typo detection against the declared set is this
 * diagnostic's whole point, and suppressing it would switch that off in the
 * editor, which is exactly where a typo gets made.
 *
 * Walks the **declaration sites** rather than reading `facetIndex`. The index is
 * keyed by bare node id and unions across scopes, so resolving a location from
 * it can only ever be a guess when two nodes in different scopes share an id
 * (ADR-927) — it pointed at whichever was visited first, which is not
 * necessarily the node that wrote the reference. A diagnostic's whole value is
 * landing on the line the author must edit, so it is derived from the site that
 * carries the property (TPL-1352 — the identifying dimension the flat index
 * drops is exactly the one this needs). `analyze()` receives the import-merged
 * tree, so walking it is still a merged-space check.
 */
function detectFacetsNotDeclared(file: KrsFile): Warning[] {
  const declared = new Set(file.facets.map((f) => f.id));
  const warnings: Warning[] = [];

  const visit = (node: KrsNode): void => {
    for (const facetId of node.facets ?? []) {
      if (declared.has(facetId)) continue;
      warnings.push({
        kind: "facet-not-declared",
        params: { subject: node.id, facetId },
        loc: node.loc,
      });
    }
    // Edges carry the property too since #2544, and for the same reason nodes
    // are walked instead of an index: the reference has to be reported on the
    // edge that wrote it. An edge has no id of its own, so it names itself by
    // its canonical base form — the same string `edge#<id>` addresses it with,
    // so what the warning prints can be pasted into a style selector.
    for (const edge of node.edges) {
      for (const facetId of edge.facets ?? []) {
        if (declared.has(facetId)) continue;
        warnings.push({
          kind: "facet-not-declared",
          params: { subject: edgeBaseId(edge), facetId },
          loc: edge.loc,
        });
      }
    }
    for (const child of node.children) visit(child);
  };
  for (const root of facetWalkRoots(file)) visit(root);

  return warnings;
}

/**
 * Return the built-in annotation name within typo distance of `name`, or
 * undefined when none is close enough. The budget scales with the
 * built-in's length so short names like `new` only match single-edit
 * slips while `migration_target` tolerates two.
 */
function closestBuiltinAnnotation(name: string, builtins: string[]): string | undefined {
  let best: { builtin: string; distance: number } | undefined;
  for (const builtin of builtins) {
    const budget = builtin.length <= 4 ? 1 : 2;
    const distance = levenshtein(name, builtin);
    if (distance <= budget && (best === undefined || distance < best.distance)) {
      best = { builtin, distance };
    }
  }
  return best?.builtin;
}

/**
 * Optimal-string-alignment distance (Levenshtein + adjacent transposition).
 * Transpositions count as one edit so the classic slip `@nwe` sits at
 * distance 1 from `new`, inside the short-name budget.
 */
function levenshtein(a: string, b: string): number {
  let prevPrev: number[] = [];
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        curr[j] = Math.min(curr[j], prevPrev[j - 2] + 1);
      }
    }
    prevPrev = prev;
    prev = curr;
  }
  return prev[b.length];
}

function detectDomainDispersal(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  // Analyze one scope unit (a system's children, or all top-level services).
  // Detection is keyed by domain id — label is a display name and must not affect identity.
  // Each system is an organizational boundary; cross-system domains are intentional.
  function detectInScope(nodes: KrsNode[]): void {
    const domainToServices = new Map<string, Set<string>>();
    // Record the location of the *last* occurrence of each dispersed domain id
    // so the warning can be anchored in the editor (LSP / Monaco) instead of
    // collapsing to the document start. The last occurrence is the one a
    // reader would treat as the "duplicate".
    const domainToLoc = new Map<string, KrsNode["loc"]>();

    function walk(node: KrsNode, parentServiceId?: string): void {
      if (node.kind === "service") {
        parentServiceId = node.id;
      }
      if (node.kind === "domain" && parentServiceId) {
        if (!domainToServices.has(node.id)) {
          domainToServices.set(node.id, new Set());
        }
        domainToServices.get(node.id)!.add(parentServiceId);
        domainToLoc.set(node.id, node.loc);
      }
      for (const child of node.children) {
        walk(child, parentServiceId);
      }
    }

    for (const node of nodes) {
      walk(node);
    }

    for (const [domainId, services] of domainToServices) {
      if (services.size > 1) {
        warnings.push({
          kind: "domain-dispersal",
          params: { domainId, services: Array.from(services) },
          loc: domainToLoc.get(domainId),
        });
      }
    }
  }

  for (const system of file.systems) {
    detectInScope(system.children);
  }
  if (file.services.length > 0) {
    detectInScope(file.services);
  }

  return warnings;
}

/** Kind + declaration location of an in-scope infra block, keyed by id. */
type InfraInScope = Map<string, { kind: "database" | "queue" | "storage"; loc: KrsNode["loc"] }>;

/**
 * The store-role tags: each one declares that the store is *not* the system of
 * record (`[index]` = derived for search, `[cache]` = recoverable by rebuilding,
 * `[analytics]` = ingested for analysis). The shared-store smells are about a
 * shared system of record, so a role-tagged store is out of scope for all of
 * them — several services reading one search index, one asset/session cache or
 * one warehouse is a normal shape, not Database-per-Service (Issues #1733,
 * #2172).
 */
const NON_SOR_ROLE_TAGS = ["index", "cache", "analytics"];

/**
 * True when the node carries a role tag *on a kind that tag applies to*. The
 * `appliesTo` check matters: `[cache]` on a `queue` already reports
 * `tag-not-applicable` ("this tag does nothing here"), and if the tag also
 * suppressed the shared-store smells it would do something after all —
 * silently, and only on the kinds where we told the author it was inert.
 */
function hasNonSorRole(node: KrsNode): boolean {
  return REFERENCE_DATA.tags.some(
    (tag) =>
      NON_SOR_ROLE_TAGS.includes(tag.name) &&
      tag.appliesTo.includes(node.kind) &&
      node.tags.includes(tag.name),
  );
}

/**
 * Collect the infra blocks (`database` / `queue` / `storage`) reachable in a
 * scope, excluding `[external]` stores and every store carrying a
 * {@link NON_SOR_ROLE_TAGS} role tag. Shared by the shared-store
 * diagnostics (`detectSharedInfraFanIn`, `detectCrossDomainStoreAccess`) so the
 * exclusion rule and the kind narrowing live in exactly one place
 * (TPL-1720 — keep the resource→store target set synchronized across
 * every consumer).
 *
 * `[external]`: the smell is about owning a shared store, not depending on a
 * managed third-party one.
 */
function collectInfraInScope(nodes: KrsNode[]): InfraInScope {
  const infra: InfraInScope = new Map();
  function walk(node: KrsNode): void {
    if (INFRA_KIND_SET.has(node.kind) && !node.tags.includes("external") && !hasNonSorRole(node)) {
      infra.set(node.id, { kind: node.kind as "database" | "queue" | "storage", loc: node.loc });
    }
    for (const child of node.children) walk(child);
  }
  for (const node of nodes) walk(node);
  return infra;
}

/**
 * Surface the shared-store "fan-in": ≥2 services with a resolved `resource`
 * dependency on the same `database` / `queue` / `storage` within one system
 * scope. Symmetric with `detectDomainDispersal` (same id under ≥2 services),
 * info-register per ADR-1386 — it states a fact (the Database-per-Service
 * smell), it is not a defect.
 *
 * Keyed on *actual sharing*, so — unlike `infra-redeclared-across-files`, which
 * keys on multi-file declaration redundancy — detection is independent of how
 * many files declared the store: `analyze()` runs on the merged `KrsFile`.
 * `[external]` stores are excluded: depending on a managed third-party store is
 * not the Database-per-Service smell.
 */
function detectSharedInfraFanIn(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  // Each system is an organizational boundary; cross-system sharing of a store
  // is intentional and not surfaced. Top-level services form their own scope.
  function detectInScope(nodes: KrsNode[]): void {
    // Infra ids in this scope, `[external]` and role-tagged stores excluded
    // (see collectInfraInScope for the rationale).
    const infraInScope = collectInfraInScope(nodes);
    const infraToServices = new Map<string, Set<string>>();

    // Resolver over this scope's entities: a bare `resource Order` that resolves
    // to `entity Order { table OrderDB.orders }` counts toward the fan-in on
    // OrderDB just as a physical `resource OrderDB.orders` would (TPL-1720
    // — keep the resource→store target set synchronized across every consumer).
    const resolver = buildEntityResolver(nodes);

    function collectRefs(node: KrsNode, parentServiceId?: string): void {
      if (node.kind === "service") {
        parentServiceId = node.id;
      }
      if (node.kind === "resource" && parentServiceId) {
        const targetId = resolver.resolve(node as ResourceNode).infraParentId;
        if (targetId !== undefined && infraInScope.has(targetId)) {
          if (!infraToServices.has(targetId)) {
            infraToServices.set(targetId, new Set());
          }
          infraToServices.get(targetId)!.add(parentServiceId);
        }
      }
      for (const child of node.children) {
        collectRefs(child, parentServiceId);
      }
    }

    for (const node of nodes) {
      collectRefs(node);
    }

    for (const [infraId, services] of infraToServices) {
      if (services.size > 1) {
        const entry = infraInScope.get(infraId)!;
        warnings.push({
          kind: "shared-infra-fan-in",
          params: { infraId, infraKind: entry.kind, services: Array.from(services) },
          loc: entry.loc,
        });
      }
    }
  }

  for (const system of file.systems) {
    detectInScope(system.children);
  }
  // Top-level (system-less) scope: services live in `file.services`, but
  // top-level infra is bucketed separately in `file.databases` / `queues` /
  // `storages` (not under any service subtree), so feed both in. Without the
  // infra buckets the canonical "top-level store shared by several services"
  // idiom (docs/spec/syntax.md) would never be collected. Each system's own
  // scope already has both, since infra and services are alike `system.children`.
  if (file.services.length > 0) {
    detectInScope([...file.services, ...file.databases, ...file.queues, ...file.storages]);
  }

  return warnings;
}

/**
 * Cross-domain store access (info): a `usecase` in domain A reads/writes an
 * infra leaf owned by a *different* domain B.
 *
 * Ownership is derived from the logical layer — a leaf `infraId.tableId` is
 * owned by every `domain` whose `entity` maps it via `table <InfraId>.<subId>`
 * (ADR-1870). No new syntax: the entity layer is the source of truth,
 * and the physical `table` never carries a domain. Keying is at **leaf
 * granularity** because sibling tables in one `database` can belong to
 * different domains — keying on the whole store would let a reach-in into a
 * co-owned store's foreign table hide behind the accessor being *an* owner of
 * *some* table there.
 *
 * Ownership is a **set** of domains: `owners(leaf) = { D : entity∈D maps leaf }`.
 * The warning fires when `accessingDomain ∉ owners(leaf)` — a single-owner
 * reach-in and a third domain touching a co-owned leaf both fire, while a
 * co-owned leaf's own owners are exempt. `[external]` and role-tagged stores
 * are excluded (symmetric with `shared-infra-fan-in`). Detection runs on the merged
 * `KrsFile` (view-independent) and is scoped per system.
 *
 * Both ownership and access key on the domain **id**, consistent with how the
 * whole model treats domain identity (domain edges, ghost domains, `handles`).
 * A consequence: when the *same* domain id is dispersed across services in one
 * system (the `domain-dispersal` info fact — allowed, not an error), the two
 * dispersed halves are treated as one logical domain, so a usecase in one half
 * reaching into a leaf owned by the other half does **not** fire here (the
 * dispersal itself is what `domain-dispersal` surfaces). Cross-*system* access
 * is out of scope: per-system scoping keeps the id comparison within a single
 * system so identical ids in different systems never conflate (ADR-1911).
 *
 * Paired with but **orthogonal** to `shared-infra-fan-in`: that keys on how
 * many services share a store; this keys on crossing an ownership boundary. The
 * two fire independently on the same store — no double counting, no mutual
 * suppression. The resource→store resolution reuses `buildEntityResolver`, kept
 * in sync with `deriveInfraEdges` / `detectSharedInfraFanIn` /
 * `detectUnassignedResources` (TPL-1720).
 * See ADR-1819 (docs/adr/1819-domain-store-ownership-diagnostic.md).
 */
function detectCrossDomainStoreAccess(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  function detectInScope(nodes: KrsNode[]): void {
    const resolver = buildEntityResolver(nodes);

    // Infra blocks in scope, `[external]` and role-tagged stores excluded
    // (shared with fan-in via collectInfraInScope). Excluded stores also contribute no
    // ownership, so an entity mapping such a leaf yields no owner and never fires.
    const infraInScope = collectInfraInScope(nodes);

    // owners: leaf key `infraId.tableId` -> set of owning domain ids.
    const owners = new Map<string, Set<string>>();
    function collectOwners(node: KrsNode, domainId?: string): void {
      const nextDomain = node.kind === "domain" ? node.id : domainId;
      if (node.kind === "entity" && nextDomain !== undefined) {
        const tableRef = (node as EntityNode).tableRef;
        if (tableRef !== undefined && infraInScope.has(tableRef.parent)) {
          const key = `${tableRef.parent}.${tableRef.child}`;
          let set = owners.get(key);
          if (!set) owners.set(key, (set = new Set()));
          set.add(nextDomain);
        }
      }
      for (const child of node.children) collectOwners(child, nextDomain);
    }
    for (const node of nodes) collectOwners(node);

    // Aggregate crossing accesses by (accessingDomain, leaf). Multiple usecases
    // in one domain touching the same foreign leaf collapse to one warning; the
    // read/write mode is the union of their accesses.
    interface Agg {
      accessingDomain: string;
      owningDomains: string[];
      infraId: string;
      infraKind: "database" | "queue" | "storage";
      tableId: string;
      hasRead: boolean;
      hasWrite: boolean;
      loc: KrsNode["loc"];
    }
    const agg = new Map<string, Agg>();

    function collectAccess(node: KrsNode, domainId?: string): void {
      const nextDomain = node.kind === "domain" ? node.id : domainId;
      if (node.kind === "resource" && nextDomain !== undefined) {
        const resolved = resolver.resolve(node as ResourceNode);
        const { infraParentId: parent, infraChildId: child } = resolved;
        if (parent !== undefined && child !== undefined && infraInScope.has(parent)) {
          const leafKey = `${parent}.${child}`;
          const ownerSet = owners.get(leafKey);
          if (ownerSet && ownerSet.size > 0 && !ownerSet.has(nextDomain)) {
            const aggKey = `${nextDomain}\0${leafKey}`;
            const ops = (node as ResourceNode).properties.operations;
            let entry = agg.get(aggKey);
            if (!entry) {
              entry = {
                accessingDomain: nextDomain,
                owningDomains: Array.from(ownerSet).sort(),
                infraId: parent,
                infraKind: infraInScope.get(parent)!.kind,
                tableId: child,
                hasRead: false,
                hasWrite: false,
                loc: node.loc,
              };
              agg.set(aggKey, entry);
            }
            entry.hasWrite = entry.hasWrite || isWriteOperation(ops);
            entry.hasRead = entry.hasRead || isReadOperation(ops);
            // Point at the last crossing occurrence (mirrors domain-dispersal).
            entry.loc = node.loc;
          }
        }
      }
      for (const child of node.children) collectAccess(child, nextDomain);
    }
    for (const node of nodes) collectAccess(node);

    for (const entry of agg.values()) {
      const mode =
        entry.hasWrite && entry.hasRead ? "readwrite" : entry.hasWrite ? "write" : "read";
      warnings.push({
        kind: "cross-domain-store-access",
        params: {
          accessingDomain: entry.accessingDomain,
          owningDomains: entry.owningDomains,
          infraId: entry.infraId,
          infraKind: entry.infraKind,
          tableId: entry.tableId,
          mode,
        },
        loc: entry.loc,
      });
    }
  }

  for (const system of file.systems) {
    detectInScope(system.children);
  }
  // Top-level (system-less) scope: a top-level domain's usecase can reach into
  // another top-level domain's store, so include `file.domains` alongside the
  // services and the separately-bucketed top-level infra.
  if (file.services.length > 0 || file.domains.length > 0) {
    detectInScope([
      ...file.services,
      ...file.domains,
      ...file.databases,
      ...file.queues,
      ...file.storages,
    ]);
  }

  return warnings;
}

/**
 * Surface a bare `resource <id>` that resolves to no store: not physical
 * dot-notation, not a unique `entity` (the canonical logical form), and not
 * `[external]` (which intentionally has no store). Resolution is model-wide, so
 * the warning is *promoted away* — with zero edits to the usecase — the moment a
 * matching `entity` is declared anywhere (`docs/adr/1870-domain-entity-modeling.md`).
 *
 * Moved here from the parser (which only sees a single declaration): the
 * entity a bare id resolves to may be declared in another domain / service.
 * An ambiguous bare id (>1 matching entity) stays unresolved and warned here;
 * its root-cause collision is reported separately by
 * `detectEntityAnchorCollisions`.
 */
function detectUnassignedResources(file: KrsFile): Warning[] {
  const resolver = buildEntityResolver([...file.systems, ...file.services, ...file.domains]);
  const warnings: Warning[] = [];

  function walk(node: KrsNode): void {
    if (node.kind === "resource") {
      const res = node as ResourceNode;
      if (
        !res.ref &&
        !res.tags.includes("external") &&
        resolver.resolve(res).entityId === undefined
      ) {
        warnings.push({
          kind: "unassigned-resource",
          params: { resourceId: res.id },
          loc: res.loc,
        });
      }
    }
    for (const child of node.children) walk(child);
  }

  for (const root of [...file.systems, ...file.services, ...file.clients, ...file.domains]) {
    walk(root);
  }
  return warnings;
}

/**
 * Node kinds that may hold a `domain` yet leave it unassigned — every kind whose
 * `canContain` lists `domain`, minus `service` (a domain under a service *is*
 * assigned). Derived from `LOGICAL_CONTAINMENT` rather than naming `system`
 * directly, so adding a domain parent to `canContain` cannot leave this detector
 * behind (TPL-2165 / TPL-2184). Today the set is exactly `{ system }`.
 */
const UNASSIGNING_DOMAIN_PARENTS: ReadonlySet<string> = new Set(
  [...LOGICAL_CONTAINMENT.entries()]
    .filter(([parent, children]) => parent !== "service" && children.has("domain"))
    .map(([parent]) => parent),
);

/**
 * A `domain` is unassigned when its parent is not a `service` — which is what
 * the warning says ("is not assigned to any service"), so the scan range is
 * decided by parent kind, not by which AST slot the node landed in.
 *
 * Two placements express that state: the file top level, licensed by the
 * top-level grammar (`top-level-declaration`, ADR-1639), and a block parent
 * drawn from `canContain` (ADR-2165) — today only `system`. Both spell the same
 * thing, so both warn: the author picks the spelling, not the meaning (#2184).
 *
 * Rendering is unaffected: the `(Unassigned)` pseudo-system still wraps only the
 * top-level form, because it gives a container to nodes that have none, and a
 * system-nested domain already has one (ADR-681).
 *
 * Nestings outside `canContain` (`client { domain … }`) already report
 * `node-not-in-context`; they are deliberately not reported twice here.
 */
function detectUnassignedDomains(file: KrsFile): Warning[] {
  const unassigned: KrsNode[] = [...file.domains];

  function walk(node: KrsNode): void {
    if (UNASSIGNING_DOMAIN_PARENTS.has(node.kind)) {
      for (const child of node.children) {
        if (child.kind === "domain") unassigned.push(child);
      }
    }
    for (const child of node.children) walk(child);
  }
  for (const root of [
    ...file.systems,
    ...file.services,
    ...(file.clients ?? []),
    ...file.domains,
  ]) {
    walk(root);
  }

  // Report in source order: `file.domains` and the nested hits come from
  // separate AST slots, so concatenating them would surface a top-level domain
  // ahead of a system-nested one declared earlier in the file.
  return unassigned
    .sort((a, b) => (a.loc?.start.offset ?? 0) - (b.loc?.start.offset ?? 0))
    .map((domain) => ({
      kind: "unassigned-domain" as const,
      params: {
        domainId: domain.id,
        ...(domain.label ? { label: domain.label } : {}),
      },
      loc: domain.loc,
    }));
}

function detectUnassignedServices(file: KrsFile): Warning[] {
  return file.services.map((service) => ({
    kind: "unassigned-service" as const,
    params: {
      serviceId: service.id,
      ...(service.label ? { label: service.label } : {}),
    },
    loc: service.loc,
  }));
}

function detectUnassignedClients(file: KrsFile): Warning[] {
  return (file.clients ?? []).map((client) => ({
    kind: "unassigned-client" as const,
    params: {
      clientId: client.id,
      ...(client.label ? { label: client.label } : {}),
    },
    loc: client.loc,
  }));
}

/**
 * Validate `handles` cross-references for `client` and `service` nodes.
 *
 * Expose rule (one-hop, recursive):
 *   A node N exposes domain D iff:
 *     1. N has a child `domain D` (self-owned), OR
 *     2. N declares `handles D` AND at least one of N's outgoing communication
 *        edges targets a node that exposes D.
 *
 * For each `handles D` declaration whose D cannot be resolved through the
 * rule, emit an `unresolved-handles` warning. The rule mirrors how the
 * design doc describes the BFF passthrough chain (client → BFF.handles →
 * backend.owns).
 *
 * Implementation notes:
 * - Operates per-system: only edges declared at the same system scope are
 *   considered. Top-level clients/services (file.clients / file.services)
 *   already produce `unassigned-*` warnings; their `handles` declarations
 *   simply have no edges to resolve through.
 * - Memoizes "exposes(node, domain)" inside a single system to keep the
 *   recursion linear in the number of nodes. The recursion is well-founded
 *   because we expand exactly one hop per call (the recursive call resolves
 *   on the *target* side of an edge, and a graph with finite nodes
 *   terminates).
 * - `delivers` is a service-side property and not an edge, so it does not
 *   participate.
 */
function detectUnresolvedHandles(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  for (const system of file.systems) {
    // Build node lookup and outgoing-edge index keyed by source id.
    const nodeById = new Map<string, KrsNode>();
    for (const child of system.children) {
      nodeById.set(child.id, child);
    }
    const outgoingByFrom = new Map<string, string[]>();
    for (const edge of system.edges) {
      let list = outgoingByFrom.get(edge.from);
      if (!list) {
        list = [];
        outgoingByFrom.set(edge.from, list);
      }
      list.push(edge.to);
    }

    // A `handles` ref is a node reference path (#2088), and the expose rule is
    // evaluated against the nodes it RESOLVES to, not the path text: a direct
    // child domain matches when the ref suffixes its full path
    // `[system, host, domain]`, and two refs name the same target when their
    // resolved sets overlap. Candidate paths of every domain directly under a
    // system-level child, computed once per system.
    const domainPaths: NodeIdPath[] = [];
    for (const host of system.children) {
      for (const child of host.children) {
        if (child.kind === "domain") domainPaths.push([system.id, host.id, child.id]);
      }
    }
    // Resolution memoized per ref: `exposes` asks for it once per declared
    // `handles` entry and again at every hop of the recursion, and each answer
    // is a linear scan of the system's domains. Loop-invariant work, hoisted.
    const resolvedByRef = new Map<string, ReadonlySet<string>>();
    const resolvedKeys = (ref: NodeIdPath): ReadonlySet<string> => {
      const key = nodePathIdentityKey(ref);
      let keys = resolvedByRef.get(key);
      if (!keys) {
        keys = new Set(
          domainPaths.filter((path) => nodePathMatchesSuffix(ref, path)).map(nodePathIdentityKey),
        );
        resolvedByRef.set(key, keys);
      }
      return keys;
    };
    const sameTarget = (a: NodeIdPath, b: NodeIdPath): boolean => {
      const bKeys = resolvedKeys(b);
      for (const key of resolvedKeys(a)) {
        if (bKeys.has(key)) return true;
      }
      return false;
    };

    // Memoization: nodeId -> ref key -> boolean. The key is the injective
    // encoding, not `nodePathKey`'s dotted join: `handles "a.b"` and
    // `handles a.b` are different refs that resolve differently, and one
    // shared key would answer for whichever was written first — the same
    // declaration-order dependence #2550 took out of the parser (TPL-1352).
    const memo = new Map<string, Map<string, boolean>>();
    const setMemo = (nodeId: string, refKey: string, value: boolean): boolean => {
      let refMap = memo.get(nodeId);
      if (!refMap) {
        refMap = new Map();
        memo.set(nodeId, refMap);
      }
      refMap.set(refKey, value);
      return value;
    };
    const inProgress = new Set<string>(); // cycle guard (key: nodeId|refKey)

    function ownsDomain(node: KrsNode, ref: NodeIdPath): boolean {
      return node.children.some(
        (c) => c.kind === "domain" && nodePathMatchesSuffix(ref, [system.id, node.id, c.id]),
      );
    }

    function declaredHandles(node: KrsNode): HandlesTarget[] | undefined {
      if (node.kind === "client" || node.kind === "service") {
        return node.properties.handles;
      }
      return undefined;
    }

    function exposes(nodeId: string, ref: NodeIdPath): boolean {
      const refKey = nodePathIdentityKey(ref);
      const cached = memo.get(nodeId)?.get(refKey);
      if (cached !== undefined) return cached;

      const node = nodeById.get(nodeId);
      if (!node) return setMemo(nodeId, refKey, false);

      // Cycle guard — treat in-progress lookups as not-yet-exposed; this
      // prevents infinite recursion on pathological graphs (e.g. A.handles X
      // pointing to B.handles X pointing back to A).
      const key = `${nodeId}|${refKey}`;
      if (inProgress.has(key)) return setMemo(nodeId, refKey, false);
      inProgress.add(key);

      // Rule 1: own — a direct child domain the ref resolves to.
      if (ownsDomain(node, ref)) {
        inProgress.delete(key);
        return setMemo(nodeId, refKey, true);
      }

      // Rule 2: re-export — the node declared a `handles` ref naming the same
      // target AND an outgoing edge target also exposes it.
      const handles = declaredHandles(node);
      if (handles?.some((own) => sameTarget(own.path, ref))) {
        const targets = outgoingByFrom.get(nodeId) ?? [];
        for (const target of targets) {
          if (exposes(target, ref)) {
            inProgress.delete(key);
            return setMemo(nodeId, refKey, true);
          }
        }
      }

      inProgress.delete(key);
      return setMemo(nodeId, refKey, false);
    }

    // Check every client / service in this system that declares `handles`.
    for (const node of system.children) {
      if (node.kind !== "client" && node.kind !== "service") continue;
      const handles = node.properties.handles;
      if (!handles || handles.length === 0) continue;

      for (const target of handles) {
        // For the declaring node itself, "exposes" recurses into rule 2 and
        // walks the outgoing edges. Self-owned domains (rule 1) take
        // precedence and are also tested there.
        if (exposes(node.id, target.path)) continue;

        warnings.push({
          kind: "unresolved-handles",
          params: {
            nodeId: node.id,
            nodeKind: node.kind,
            domainId: nodePathKey(target.path),
          },
          // The reference's own range, not the node's: `handles A, B` must
          // point at the entry that failed the expose rule, exactly as
          // `unresolved-realizes` points at one target of a comma list.
          loc: target.loc,
        });
      }
    }
  }

  return warnings;
}

function detectUnassignedDatabases(file: KrsFile): Warning[] {
  return (file.databases ?? []).map((db) => ({
    kind: "unassigned-database" as const,
    params: {
      databaseId: db.id,
      ...(db.label ? { label: db.label } : {}),
    },
    loc: db.loc,
  }));
}

function detectUnassignedQueues(file: KrsFile): Warning[] {
  return (file.queues ?? []).map((q) => ({
    kind: "unassigned-queue" as const,
    params: {
      queueId: q.id,
      ...(q.label ? { label: q.label } : {}),
    },
    loc: q.loc,
  }));
}

function detectUnassignedStorages(file: KrsFile): Warning[] {
  return (file.storages ?? []).map((s) => ({
    kind: "unassigned-storage" as const,
    params: {
      storageId: s.id,
      ...(s.label ? { label: s.label } : {}),
    },
    loc: s.loc,
  }));
}

function detectUnassignedUsecases(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  function walkServiceChildren(nodes: KrsNode[]): void {
    for (const node of nodes) {
      if (node.kind === "service") {
        for (const child of node.children) {
          if (child.kind === "usecase") {
            warnings.push({
              kind: "unassigned-usecase",
              params: { usecaseId: child.id },
              loc: child.loc,
            });
          }
        }
        // recurse into domains to find nested services (not expected, but safe)
        walkServiceChildren(node.children.filter((c) => c.kind !== "usecase"));
      }
    }
  }

  for (const system of file.systems) {
    walkServiceChildren(system.children);
  }
  walkServiceChildren(file.services);

  return warnings;
}

/**
 * The `entity` deep-link view token addresses a model-wide namespace of
 * {all domain ids} ∪ {all entity ids} (a domain id opens that domain's entity
 * view; an entity id focuses that entity). An id claimed by two targets in
 * that namespace makes `#krs-entity-<id>` ambiguous and produces duplicate DOM
 * ids in the bundled static SVG. Flag it — but only when an entity is involved
 * (two same-id domains across services are the existing domain-dispersal fact,
 * addressed elsewhere), and only for a genuine cross-domain clash: two same-id
 * entities under ONE domain are already a `duplicate-node-id-parent` error, so
 * they are not double-reported here.
 */
function detectEntityAnchorCollisions(file: KrsFile): Warning[] {
  const domainIds = new Set<string>();
  // entity id -> set of DISTINCT owning domain NODES (object identity, not id:
  // the same domain id dispersed across systems is two distinct domain nodes,
  // hence two distinct entity anchors).
  const entityOwnerNodes = new Map<string, Set<KrsNode>>();
  const entityFirstLoc = new Map<string, KrsNode["loc"]>();

  const visit = (node: KrsNode, parentDomain: KrsNode | undefined): void => {
    if (node.kind === "domain") {
      domainIds.add(node.id);
    } else if (node.kind === "entity") {
      let owners = entityOwnerNodes.get(node.id);
      if (!owners) {
        owners = new Set<KrsNode>();
        entityOwnerNodes.set(node.id, owners);
        entityFirstLoc.set(node.id, node.loc);
      }
      if (parentDomain !== undefined) owners.add(parentDomain);
    }
    const childDomain = node.kind === "domain" ? node : parentDomain;
    for (const child of node.children) visit(child, childDomain);
  };

  for (const system of file.systems) {
    for (const child of system.children) visit(child, undefined);
  }
  for (const service of file.services) visit(service, undefined);
  for (const domain of file.domains) visit(domain, undefined);

  const warnings: Warning[] = [];
  for (const [id, loc] of entityFirstLoc) {
    // Two same-id entities under ONE domain node are already a
    // duplicate-node-id-parent error — count DISTINCT owning domain nodes so
    // we warn only on a genuine cross-domain / entity-vs-domain anchor clash.
    const spansMultipleDomains = (entityOwnerNodes.get(id)?.size ?? 0) > 1;
    const clashesWithDomain = domainIds.has(id);
    if (spansMultipleDomains || clashesWithDomain) {
      warnings.push({ kind: "entity-anchor-collision", params: { id }, loc });
    }
  }
  return warnings;
}

function detectStyleConflicts(sheets: StyleSheet[], systemSheetCount = 1): Warning[] {
  // Skip system sheets (built-in + any injected theme sheets) — they are designed to be overridden.
  // Only detect conflicts among user sheets (index systemSheetCount+).
  const userSheets = sheets.slice(systemSheetCount);
  if (userSheets.length <= 1) return [];
  const warnings: Warning[] = [];

  // Group rules by serialized selector, tracking which user sheet they came from
  const selectorToSheets = new Map<string, Set<number>>();

  for (let i = 0; i < userSheets.length; i++) {
    for (const rule of userSheets[i].rules) {
      // The Tidy formatter's spelling, not a second one written here: the two
      // had drifted, and this side put the id before the kind (`team#Platform`
      // read as `#Platformteam`) and dropped `edge#<id>` / `boundary#<id>`
      // entirely, fusing rules that named *different* boundaries into one
      // `boundary` key and reporting a conflict that did not exist (TPL-2234).
      const key = formatSelector(rule.selector);
      if (!selectorToSheets.has(key)) {
        selectorToSheets.set(key, new Set());
      }
      selectorToSheets.get(key)!.add(i);
    }
  }

  for (const [selector, sheetIndices] of selectorToSheets) {
    if (sheetIndices.size > 1) {
      warnings.push({
        kind: "style-conflict",
        params: { selector, sheetIndices: Array.from(sheetIndices) },
      });
    }
  }

  return warnings;
}

function detectMissingProperties(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  for (const deploy of file.deploys) {
    for (const node of deploy.nodes) {
      // `store` is a managed data store with no runtime form (its concrete
      // tech is the free-text `type`), so it is exempt from the runtime nudge.
      if (node.kind !== "store" && !node.properties.runtime) {
        warnings.push({
          kind: "missing-runtime",
          params: { nodeId: node.id },
          loc: node.loc,
        });
      }
      if (!node.properties.realizes?.length) {
        warnings.push({
          kind: "missing-realizes",
          params: { nodeId: node.id },
          loc: node.loc,
        });
      }
    }
  }

  return warnings;
}

/**
 * Validate that every `realizes <target>` on a deploy node points to a
 * known logical-side service or domain id.
 *
 * Closes the validation gap reported in Issue #907: previously the parser
 * accepted any identifier after `realizes` and silently disconnected the
 * physical node from the logical layer when the target was a typo. Mirrors
 * the pattern used by `detectInvalidOwns`.
 *
 * Notes:
 * - Skips deploy nodes with empty `realizes` — those are already covered
 *   by `missing-realizes`.
 * - Cross-file imports work transparently because `compileProject` merges
 *   imported files into a single `KrsFile` before analysis runs.
 * - The valid-id set covers `service` and `domain` ids reachable through
 *   `file.systems` plus the top-level `file.services` / `file.domains`
 *   buckets, matching how `realizes` is consumed by `extractDeployView`.
 */
function detectUnresolvedRealizes(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  // A target is a node reference path (#2088), resolved by the suffix rule
  // over every declared node of a realizable kind: services / domains /
  // clients and infra blocks (database / queue / storage) at any depth. A
  // deploy unit may realize a shared store to record its physical form (e.g.
  // a `store` unit realizing a `database`); see ADR-1632. A client is also a
  // deployable logical node (a `war` / `assets` bundle realizes a `client`
  // SPA), so it is a valid target too; see ADR-1720. Leaf sub-resources
  // (table / queue-item / bucket) are NOT valid targets, and a ref whose
  // every match is such a kind keeps drawing this warning, exactly as the
  // old flat id set did.
  const declared = collectDeclaredNodePaths(file);
  // The shared enumeration, not a second copy of it: the ambiguity check in
  // parser/reference-validation.ts is the other reader (TPL-1720).
  const realizable = (ref: NodeIdPath): boolean =>
    resolveDeclaredRef(declared, ref).some((m) => REALIZES_TARGET_KIND_SET.has(m.kind));

  for (const deploy of file.deploys) {
    for (const node of deploy.nodes) {
      const realizes = node.properties.realizes;
      if (!realizes || realizes.length === 0) continue;
      for (const target of realizes) {
        if (!realizable(target.path)) {
          warnings.push({
            kind: "unresolved-realizes",
            params: {
              deployNodeId: node.id,
              deployBlockId: deploy.id,
              target: nodePathKey(target.path),
            },
            // The identifier's own range, not the node's: a unit realizing
            // several targets — whether written as repeated lines or one comma
            // list (#2167) — must point at the target that failed to resolve.
            loc: target.loc,
          });
        }
      }
    }
  }

  return warnings;
}

function detectInvalidOwns(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  // Resolution goes through the shared declared-node multimap and suffix rule
  // (#2088), so this check and the existence check cannot drift apart on what
  // an `owns` ref names. `invalid-owns` reports a *kind*, so it may only speak
  // about a ref that resolves to at least one node: "declared nowhere" is
  // `owns-target-not-found`'s verdict, and reporting both for a plain typo
  // contradicted this diagnostic's own definition (#2410). Systems are in the
  // pool so `owns <systemId>` reads as the kind refusal it is (#2442).
  //
  // The fire condition is "no resolved target is ownable" (OWNS_TARGET_KIND_SET,
  // the one enumeration — TPL-1720): when one of the resolved nodes IS ownable,
  // nothing fires at all. The silence is the intent: some node this ref names
  // can be owned, so there is no refusal to report. Only a ref whose every
  // target is unownable reaches the message, and the first target's kind
  // labels it — with several unownable kinds either label makes the same point.
  const declared = collectDeclaredNodePaths(file);

  function checkTeams(teams: TeamNode[]): void {
    for (const team of teams) {
      for (const ref of team.properties.owns) {
        const matches = resolveDeclaredRef(declared, ref);
        if (matches.length === 0) continue;
        if (matches.some((m) => m.kind !== "system" && OWNS_TARGET_KIND_SET.has(m.kind))) {
          continue;
        }
        warnings.push({
          kind: "invalid-owns",
          params: {
            teamId: team.id,
            ownedId: nodePathKey(ref),
            ownedKind: matches[0].kind as LogicalNodeKind,
          },
          loc: team.loc,
        });
      }
      checkTeams(team.children.filter((c): c is TeamNode => c.kind === "team"));
    }
  }

  for (const org of file.organizations) {
    checkTeams(org.teams);
  }

  return warnings;
}

/**
 * A qualified target on a `system`-scope edge, judged against the shared
 * endpoint rule (#2577) rather than against `split(".")` into exactly two.
 *
 * The two verdicts split on where the reference lands, not on how long it is:
 *
 * - resolves **nowhere** → `cross-system-ref-unresolved`;
 * - resolves **into another top-level system** → `cross-system-ref-implicit-external`.
 *
 * A path that resolves inside the declaring system (`Checkout.Payment`, a
 * service and its domain) is neither — before slice E it could not resolve at
 * all and fell here as an unresolved cross-system ref; now the suffix rule
 * reaches it and it simply renders. A path that resolves only somewhere the
 * scope cannot see is `edge-endpoint-not-at-scope`'s, not this detector's.
 */
function detectCrossSystemRefs(file: KrsFile, scopeIndex: () => EdgeEndpointIndex): Warning[] {
  const warnings: Warning[] = [];

  for (const system of file.systems) {
    // Build set of system IDs with [external] tag explicitly declared as children of this system.
    // A child node with the same ID as the referenced system suppresses the implicit-external warning.
    const explicitExternalIds = new Set(
      system.children.filter((c) => c.tags.includes("external")).map((c) => c.id),
    );

    for (const edge of system.edges) {
      if (!edge.to.includes(".")) continue;

      const ref = edgeEndpointRef(edge.to);
      const systemId = ref[0];

      // Suppressed if the referenced system ID is explicitly declared as [external] in the source system
      if (explicitExternalIds.has(systemId)) continue;

      const resolution = resolveEdgeEndpoint(scopeIndex(), system, ref);
      if (resolution.inScope.length === 0) {
        if (resolution.matches.length === 0) {
          warnings.push({
            kind: "cross-system-ref-unresolved",
            params: { ref: edge.to },
            loc: edge.loc,
          });
        }
        continue;
      }

      // Read the target system off the *resolved* path rather than off the
      // reference's first segment: an in-scope qualified endpoint is
      // root-anchored, so the two agree, and reading the resolution keeps them
      // agreeing if the anchoring rule is ever loosened.
      const targetSystemId = resolution.inScope[0].path[0];
      const referencedSystem = file.systems.find((s) => s.id === targetSystemId);
      if (referencedSystem !== undefined && referencedSystem !== system) {
        warnings.push({
          kind: "cross-system-ref-implicit-external",
          params: {
            ref: edge.to,
            sourceSystemId: system.id,
            sourceNodeId: edge.from,
            targetSystemId,
          },
          loc: edge.loc,
        });
      }
    }
  }

  return warnings;
}

/**
 * §S6: an authored edge whose endpoint id exists nowhere in the merged model
 * is dropped during view extraction (the resolved endpoint node is kept —
 * TPL-2170). This surfaces that otherwise-silent drop as a warning.
 *
 * Only authored edges are inspected: synthetic edges (implicit service edges,
 * usecase→resource edges) are produced during view extraction and never appear
 * on `KrsFile`. Cross-system dotted refs (`Sys.Svc`) are skipped — they carry a
 * "." and are handled by `detectCrossSystemRefs`.
 */
function detectUnresolvedEdgeEndpoints(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  // Universe of every node id in the merged model, all kinds, all depths.
  // `nodePathIndex` can't be reused here — it only indexes service / domain.
  const allIds = new Set<string>();
  const collectIds = (node: KrsNode): void => {
    allIds.add(node.id);
    for (const child of node.children) collectIds(child);
  };
  for (const system of file.systems) {
    for (const child of system.children) collectIds(child);
  }
  for (const node of [
    ...file.services,
    ...file.domains,
    ...file.clients,
    ...file.databases,
    ...file.queues,
    ...file.storages,
  ]) {
    collectIds(node);
  }

  const checkEdges = (edges: KrsEdge[]): void => {
    for (const edge of edges) {
      for (const endpoint of [edge.from, edge.to]) {
        // Cross-system dotted refs are validated by detectCrossSystemRefs.
        if (endpoint.includes(".")) continue;
        if (!allIds.has(endpoint)) {
          warnings.push({
            kind: "unresolved-edge-endpoint",
            params: { from: edge.from, to: edge.to, unresolvedId: endpoint },
            loc: edge.loc,
          });
        }
      }
    }
  };
  const walkEdges = (node: KrsNode): void => {
    checkEdges(node.edges);
    for (const child of node.children) walkEdges(child);
  };
  for (const system of file.systems) {
    checkEdges(system.edges);
    for (const child of system.children) walkEdges(child);
  }
  for (const node of [
    ...file.services,
    ...file.domains,
    ...file.clients,
    ...file.databases,
    ...file.queues,
    ...file.storages,
  ]) {
    walkEdges(node);
  }

  return warnings;
}

/**
 * An authored edge renders only where its endpoints stand side by side: the
 * view of the block it is declared in, or — for an edge anchored inside a
 * child block — the view that draws that child as a node (`view-extract.ts`,
 * `collectAnchoredPeerEdges`; ADR-2223). Either way extraction keeps the edges
 * whose two endpoints are peers at that scope, so an endpoint resolving to a
 * node *outside* the peer set drops the edge from every view. Before #2075 the
 * drop was silent; this surfaces it.
 *
 * Peer-ness is the only condition this detector owns. Extraction applies a
 * second one — the edge must start at the block that declares it — but that
 * one already has its diagnostic upstream (`edge-source-mismatch`, raised by
 * the parser), so a mismatched edge is dropped from every view without ever
 * reaching here (ADR-2501). The two conditions are one rule between them; this
 * is the half whose signal would otherwise be missing.
 *
 * The peer set is per **node instance**, mirroring what the renderer does:
 * `layout.ts`'s multi-system path draws a system's edge only when both
 * endpoints are in that system's own `idSet`. Unioning peers by id would hide
 * two real drops — two same-id `system` blocks in one file (they stay separate
 * AST nodes; only *imported* ones merge into one node via `import-resolver.ts`,
 * where per-instance peers are already correct), and the same `domain` id
 * dispersed across two services, which the entity view keeps distinct by node
 * identity.
 *
 * Skips (owned by other detectors) and the exemptions are documented on
 * `WarningParamsByKind["edge-endpoint-not-at-scope"]`.
 */
function detectEdgeEndpointsNotAtScope(
  file: KrsFile,
  scopeIndex: () => EdgeEndpointIndex,
): Warning[] {
  const warnings: Warning[] = [];

  const topLevel = [
    ...file.services,
    ...file.domains,
    ...file.clients,
    ...file.databases,
    ...file.queues,
    ...file.storages,
  ];

  /**
   * id -> the node it resolves to (first declaration wins). Existence is a
   * model-wide question — the same question `unresolved-edge-endpoint` asks —
   * so this index stays keyed by id even though the peer sets are not.
   */
  const nodeById = new Map<string, KrsNode>();
  /** node instance -> its declaring parent instance. */
  const parentOf = new Map<KrsNode, KrsNode>();

  const index = (node: KrsNode): void => {
    if (!nodeById.has(node.id)) nodeById.set(node.id, node);
    for (const child of node.children) {
      parentOf.set(child, node);
      index(child);
    }
  };
  for (const system of file.systems) index(system);
  for (const node of topLevel) index(node);

  // `peers(C)` — and the orphan-frame reasoning behind it — moved to
  // `edge-endpoint.ts` with slice E (#2577), so this detector and the ghost
  // resolution in `view-extract.ts` read one definition. The bare set is
  // unchanged; what the qualified branch below adds is a second condition of
  // its own, root-anchoring, which does not consult the container at all.
  const check = (container: KrsNode): void => {
    for (const edge of container.edges) {
      for (const endpointId of [edge.from, edge.to]) {
        const ref = edgeEndpointRef(endpointId);
        const resolution = resolveEdgeEndpoint(scopeIndex(), container, ref);
        if (resolution.ambiguous) {
          warnings.push({
            kind: "edge-target-ambiguous",
            params: {
              path: nodePathKey(ref),
              candidates: resolution.ambiguous.map((c) => ({
                kind: c.kind,
                path: nodePathKey(c.path),
              })),
            },
            loc: edge.loc,
          });
        }
        if (resolution.inScope.length > 0) continue;
        // Absent from the model entirely — `unresolved-edge-endpoint` (bare) or
        // `cross-system-ref-unresolved` (qualified) owns that report.
        if (resolution.matches.length === 0) continue;
        if (ref.length > 1) {
          // A qualified relation inside an `entity` block belongs to the entity
          // view, which resolves it against the domains of one system and draws
          // the foreign entity as a ghost (ADR-1911). That pool is not this
          // detector's, so a verdict here would report an edge the entity view
          // does render — the silence ADR-2075 gave dotted refs, kept for the
          // one site that still owns its own resolution.
          if (container.kind === "entity") continue;
          // The path resolves, but it is not anchored at a top-level root. The
          // fix is to spell it from a root, not to move the edge, so the
          // message takes the `qualified` variant.
          warnings.push({
            kind: "edge-endpoint-not-at-scope",
            params: {
              from: edge.from,
              to: edge.to,
              endpointId,
              endpointKind: resolution.matches[0].kind,
              scopeId: container.id,
              scopeKind: container.kind,
              qualified: true,
              candidates: resolution.matches.map((m) => nodePathKey(m.path)),
            },
            loc: edge.loc,
          });
          continue;
        }
        // Bare: ADR-2075's judgement verbatim, down to reading the endpoint's
        // kind off the model-wide first-declaration-wins index.
        const endpoint = nodeById.get(endpointId);
        if (!endpoint) continue;
        // A domain depending on any other domain is derived up to a service
        // edge (`deriveImplicitServiceEdges`), so it does render.
        if (container.kind === "domain" && endpoint.kind === "domain") continue;
        const owner = parentOf.get(endpoint);
        const ownerId = owner?.id;
        const ownerKind = owner?.kind;
        warnings.push({
          kind: "edge-endpoint-not-at-scope",
          params: {
            from: edge.from,
            to: edge.to,
            endpointId,
            endpointKind: endpoint.kind,
            ...(ownerId !== undefined ? { ownerId } : {}),
            ...(ownerKind !== undefined ? { ownerKind } : {}),
            scopeId: container.id,
            scopeKind: container.kind,
          },
          loc: edge.loc,
        });
      }
    }
    for (const child of container.children) check(child);
  };
  for (const system of file.systems) check(system);
  for (const node of topLevel) check(node);

  return warnings;
}

function detectCyclicDependencies(file: KrsFile): Warning[] {
  const warnings: Warning[] = [];

  function detectInEdges(edges: KrsEdge[]): void {
    // Only inspect sync edges — async cycles are intentional (eventual consistency patterns)
    const syncEdges = edges.filter((e) => e.kind === "sync");
    if (syncEdges.length === 0) return;

    // Build adjacency map: node -> outgoing sync edges
    const adj = new Map<string, KrsEdge[]>();
    for (const edge of syncEdges) {
      if (!adj.has(edge.from)) adj.set(edge.from, []);
      adj.get(edge.from)!.push(edge);
    }

    const WHITE = 0,
      GRAY = 1,
      BLACK = 2;
    const color = new Map<string, number>();

    function dfs(node: string, path: string[]): void {
      color.set(node, GRAY);
      const currentPath = [...path, node];

      for (const edge of adj.get(node) ?? []) {
        const neighbor = edge.to;

        // Self-reference
        if (neighbor === node) {
          edge.cyclic = true;
          warnings.push({
            kind: "cyclic-dependency",
            params: { cyclePath: [node, node] },
            loc: edge.loc,
          });
          continue;
        }

        const neighborColor = color.get(neighbor) ?? WHITE;
        if (neighborColor === GRAY) {
          // Back edge found — mark this edge and all edges in the cycle
          edge.cyclic = true;
          const cycleStartIdx = currentPath.indexOf(neighbor);
          const cyclePath = [...currentPath.slice(cycleStartIdx), neighbor];

          // Mark all edges that form the cycle
          for (let i = 0; i < cyclePath.length - 1; i++) {
            const from = cyclePath[i];
            const to = cyclePath[i + 1];
            for (const cycleEdge of syncEdges.filter((se) => se.from === from && se.to === to)) {
              cycleEdge.cyclic = true;
            }
          }

          warnings.push({
            kind: "cyclic-dependency",
            params: { cyclePath },
            loc: edge.loc,
          });
        } else if (neighborColor === WHITE) {
          dfs(neighbor, currentPath);
        }
      }

      color.set(node, BLACK);
    }

    // Collect all nodes that appear in sync edges
    const nodes = new Set<string>();
    for (const edge of syncEdges) {
      nodes.add(edge.from);
      nodes.add(edge.to);
    }

    for (const node of nodes) {
      if ((color.get(node) ?? WHITE) === WHITE) {
        dfs(node, []);
      }
    }
  }

  function walkNodes(nodes: KrsNode[]): void {
    for (const node of nodes) {
      // Entity relation edges are associations, not dependencies. Self-loops
      // (a self-referential FK) and mutual references are ordinary ER shapes,
      // not architectural cycles — exclude them from cycle detection.
      if (node.kind !== "entity") detectInEdges(node.edges);
      walkNodes(node.children);
    }
  }

  for (const system of file.systems) {
    detectInEdges(system.edges);
    walkNodes(system.children);
  }

  return warnings;
}
